import React from 'react';
import { RotateCcw, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const relTime = (ts) => {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  if (Number.isNaN(diff)) return '';
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr${h > 1 ? 's' : ''} ago`;
  const d = Math.floor(h / 24);
  return `${d} day${d > 1 ? 's' : ''} ago`;
};

/**
 * Shown when a document's shared draft was auto-resumed on select, so an
 * operator continues a colleague's in-progress work instead of starting fresh.
 * Includes a soft concurrency warning and a "Start Fresh" escape hatch that
 * discards the shared draft.
 */
export const ResumedDraftBanner = ({ draft, onStartFresh, busy = false, className = '' }) => {
  if (!draft) return null;
  const who = draft.last_updated_by_name || draft.created_by_name || 'a colleague';
  const count = draft.bundle_count || 0;
  return (
    <div
      className={`rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 flex items-start justify-between gap-3 ${className}`}
      data-testid="resumed-draft-banner"
    >
      <div className="flex items-start gap-2 min-w-0">
        <RotateCcw className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-amber-900" data-testid="resumed-draft-title">
            Resumed shared draft — {count} bundle{count === 1 ? '' : 's'} so far
          </p>
          <p className="text-xs text-amber-700 flex items-center gap-1 mt-0.5" data-testid="resumed-draft-warning">
            <AlertTriangle className="w-3 h-3 shrink-0" />
            {who} worked on this {relTime(draft.updated_at)} — you are continuing the same draft.
          </p>
        </div>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={onStartFresh}
        disabled={busy}
        className="shrink-0 border-amber-400 text-amber-800 hover:bg-amber-100"
        data-testid="resumed-draft-start-fresh"
      >
        Start Fresh
      </Button>
    </div>
  );
};

export default ResumedDraftBanner;
